import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getCurrentChannel } from '../config';
import useCustomFetchApi from '../utils/customFetchApi';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import './Login.scss';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const { login } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  // Check for reset token in URL
  useEffect(() => {
    // Check if we're on the reset-password route
    if (location.pathname.includes('/reset-password')) {
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');
      if (token) {
        setResetToken(token);
        setShowResetPassword(true);
      }
    }
  }, [location.search, location.pathname]);
  
  // Sync language with URL for login routes not wrapped by AppContent language effect
  useEffect(() => {
    try {
      const segments = (location.pathname || '').split('/').filter(Boolean);
      const first = segments[0];
      const supported = ['en','my','ar'];
      if (first && supported.includes(first) && i18n.language !== first) {
        i18n.changeLanguage(first);
        // Update html lang/dir for standalone login visits
        if (typeof document !== 'undefined') {
          document.documentElement.lang = first;
          document.documentElement.dir = first === 'ar' ? 'rtl' : 'ltr';
        }
      }
    } catch(_) { /* ignore */ }
  }, [location.pathname, i18n]);
  
  const showNotification = (message, type = 'success') => {
    // Simple notification - you can enhance this
//    console.log(`${type}: ${message}`);
  };
  
  const api = useCustomFetchApi(showNotification);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      }, { skipAuth: true }); // Skip auth for login endpoint

      if (response.user && response.token) {
        login(response.user, response.token);
        // Always redirect to malaysia channel for now
        navigate(`/${i18n.language}/malaysia/dashboard`);
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/forgot-password', {
        email: forgotPasswordEmail,
      }, { skipAuth: true });

      if (response.success) {
        setForgotPasswordSuccess(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/reset-password', {
        token: resetToken,
        newPassword: newPassword,
      }, { skipAuth: true });

      if (response.success) {
        setResetSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          setShowResetPassword(false);
          setResetSuccess(false);
          navigate(location.pathname.replace(/\?.*$/, '')); // Remove token from URL
        }, 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Render reset password form if token is present
  if (showResetPassword) {
    return (
      <div className="login-container">
        <div className="login-card">
          <button
            type="button"
            className="login-back-button"
            onClick={() => {
              setShowResetPassword(false);
              navigate(location.pathname.replace(/\?.*$/, ''));
            }}
            aria-label="Go back"
          >
            {t('login.backToLogin', '← Back to Login')}
          </button>
          <h2>{t('login.resetPasswordTitle', 'Reset Your Password')}</h2>
          
          {resetSuccess && (
            <div className="success-message">
              {t('login.resetPasswordSuccess', 'Password has been reset successfully! Redirecting to login...')}
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
          
          {!resetSuccess && (
            <form onSubmit={handleResetPassword} className="login-form">
              <div className="form-group">
                <label htmlFor="newPassword">{t('login.newPassword', 'New Password')}</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder={t('login.newPasswordPlaceholder', 'Enter new password (min 8 characters)')}
                  minLength={8}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">{t('login.confirmPassword', 'Confirm Password')}</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder={t('login.confirmPasswordPlaceholder', 'Confirm new password')}
                  minLength={8}
                />
              </div>
              
              <button 
                type="submit" 
                className="login-button"
                disabled={loading}
              >
                {loading ? t('login.resetting', 'Resetting...') : t('login.resetPasswordButton', 'Reset Password')}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Render forgot password form
  if (showForgotPassword) {
    return (
      <div className="login-container">
        <div className="login-card">
          <button
            type="button"
            className="login-back-button"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotPasswordSuccess(false);
              setError('');
            }}
            aria-label="Go back"
          >
            {t('login.backToLogin', '← Back to Login')}
          </button>
          <h2>{t('login.forgotPasswordTitle', 'Forgot Password')}</h2>
          
          {forgotPasswordSuccess && (
            <div className="success-message">
              {t('login.forgotPasswordSuccess', 'If an account with that email exists, a password reset link has been sent. Please check your email.')}
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
          
          {!forgotPasswordSuccess && (
            <form onSubmit={handleForgotPassword} className="login-form">
              <div className="form-group">
                <label htmlFor="forgotEmail">{t('login.email', 'Email Address')}</label>
                <input
                  type="email"
                  id="forgotEmail"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder={t('login.emailPlaceholder', 'Enter your email')}
                />
              </div>
              
              <button 
                type="submit" 
                className="login-button"
                disabled={loading}
              >
                {loading ? t('login.sending', 'Sending...') : t('login.sendResetLink', 'Send Reset Link')}
              </button>
            </form>
          )}
          
          <div className="login-footer" style={{marginTop:'1.25rem'}}>
            <p style={{fontSize: '0.85rem', color: '#cbd5e1'}}>
              {t('login.resetLinkInfo', "You'll receive an email with a link to reset your password.")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render login form (default)
  return (
    <div className="login-container">
      <div className="login-card">
        <button
          type="button"
          className="login-back-button"
          onClick={() => {
            // Prefer explicit lang/channel from params if present
            const lang = params.lang && ['en','my','ar'].includes(params.lang) ? params.lang : i18n.language || 'en';
            const channel = params.channel;
            if (channel) {
              navigate(`/${lang}/${channel}`);
            } else {
              // Attempt to parse from current path segments as fallback
              const segs = (location.pathname || '').split('/').filter(Boolean);
              const langSeg = segs[0] && ['en','my','ar'].includes(segs[0]) ? segs[0] : lang;
              const channelSeg = segs[1] && !['login','dashboard','privacy','terms','payment'].includes(segs[1]) ? segs[1] : null;
              if (channelSeg) {
                navigate(`/${langSeg}/${channelSeg}`);
              } else {
                navigate(`/${langSeg}`);
              }
            }
          }}
          aria-label="Go back"
        >
          ← Back
        </button>
  <h2>{t('login.title','Login to Your Profile')}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">{t('login.email','Email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder={t('login.emailPlaceholder','Enter your email')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('login.password','Password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder={t('login.passwordPlaceholder','Enter your password')}
            />
            <div style={{textAlign: 'right', marginTop: '0.5rem'}}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-link"
                disabled={loading}
              >
                {t('login.forgotPassword', 'Forgot Password?')}
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? t('login.loggingIn','Logging in...') : t('login.button','Login')}
          </button>
        </form>
        
        <div className="login-footer" style={{marginTop:'1.25rem'}}>
          <p style={{marginBottom:'0.75rem'}}>{t('login.noAccount','Don\'t have an account?')} <a href="/">{t('login.createProfile','Create your profile')}</a></p>
          <div className="login-hint" style={{fontSize: '0.85rem', lineHeight: 1.45, color: '#cbd5e1'}}>
            <strong>{t('login.hintLine1','First time logging in? Use the same email you used during payment.')}</strong><br/>
            {t('login.hintLine2','Your password was sent in the confirmation email.')}<br/>
            <span style={{opacity:0.9}}>{t('login.needHelp','Need help?')} <a href="mailto:support@ylived.com" style={{color:'#00d4ff'}}>{t('login.supportEmail','support@ylived.com')}</a></span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
